import { useEffect, useRef, useState } from "react";
import { isBadgeSoundEnabled } from "./useBadgeSoundPreference";
import { playBadgeSound as playBadgeSoundBase } from "@/lib/audio";

/**
 * Play badge sound if enabled in preferences
 */
const playBadgeSound = () => {
  if (!isBadgeSoundEnabled()) return;
  playBadgeSoundBase();
};

/**
 * Hook that detects value changes and returns a pulse state for animation
 * @param value - The current value to track
 * @param options - Optional configuration
 * @param options.pulseDuration - Duration of the pulse animation in ms (default 600ms)
 * @param options.playSoundOnIncrease - Play sound when value increases (default true)
 * @returns isPulsing - Whether the pulse animation should be active
 */
export function useBadgePulse(
  value: number,
  options?: { pulseDuration?: number; playSoundOnIncrease?: boolean },
): boolean {
  const { pulseDuration = 600, playSoundOnIncrease = true } = options || {};
  const [isPulsing, setIsPulsing] = useState(false);
  const previousValue = useRef<number | null>(null);
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Skip animation on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousValue.current = value;
      return;
    }

    // Only trigger pulse if value actually changed
    if (previousValue.current !== null && previousValue.current !== value) {
      setIsPulsing(true);

      // Play sound only when value increases
      if (playSoundOnIncrease && value > previousValue.current) {
        playBadgeSound();
      }

      const timer = setTimeout(() => {
        setIsPulsing(false);
      }, pulseDuration);

      previousValue.current = value;
      return () => clearTimeout(timer);
    }

    previousValue.current = value;
  }, [value, pulseDuration, playSoundOnIncrease]);

  return isPulsing;
}
